import {QueryFunction, QueryKey, UseQueryOptions, useQuery} from '@tanstack/react-query';

import {getQueryKey} from '../lib/helpers/queryKeys';
import {convertColumnsToString} from '../lib/helpers/string';
import {supabase} from '../lib/utils/supabaseClass';
import {Database} from '../types/database.types';
import {ColumnSelectionT, CombinedColumnsT} from '../types/query.types';
import {AllTablesT, FlattenT} from '../types/utility.types';

/*******************************************************************
 * Types
 *******************************************************************/
// Type for the response of the useItemQuery hook
export type UseItemQueryResponseT<T extends keyof AllTablesT, C extends ColumnSelectionT<T>[]> = CombinedColumnsT<T, C>;

export type UseItemQueryPropsT<
  T extends keyof Database['public']['Tables'],
  C extends ColumnSelectionT<T>[] = ['*'],
  TData = CombinedColumnsT<T, C>,
  TError extends Error = Error,
  TQueryFnData = CombinedColumnsT<T, C>,
  TQueryKey extends QueryKey = QueryKey,
> = FlattenT<
  Partial<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>> & {
    table: T;
    id: number;
    columns?: C;
    enabled?: boolean;
    idColumn?: string;
  }
>;

/**
 * Fetches an item row from the database
 * @param {T} table - The table to fetch from
 * @param {number} id - The id of the item to fetch
 * @param {C} columns - The columns to select
 * @returns {Promise<CombinedColumnsT<T, C> | null>} - The item row
 */
async function fetchItem<T extends keyof Database['public']['Tables'], C extends ColumnSelectionT<T>[]>(
  table: T,
  id: number,
  columns: C,
  idColumn: string = `${table.toLowerCase()}_id`
) {
  return (
    supabase
      .from(table)
      .select<string, CombinedColumnsT<T, C>>(convertColumnsToString(columns), {count: 'exact'})
      // @ts-ignore - TODO: fix this, id  cannot be correctly typed
      .eq(idColumn, id)
      .single<CombinedColumnsT<T, C>>()
      .then(({data, error}) => {
        if (error) throw error;
        if (!data) throw new Error('No data returned');
        return data;
      })
  );
}

/**
 * Fetches a single item from the database
 * @param {T} table - The table to fetch from
 * @param {number} id - The id of the item to fetch
 * @param {boolean} enabled - Whether the query is enabled
 * @param {C} columns - The columns to select
 * @returns {ReturnType<typeof useQuery<CombinedColumnsT<T, C>>>} the query
 */
export const useItemQuery = <T extends keyof Database['public']['Tables'], C extends ColumnSelectionT<T>[] = ['*']>({
  table,
  id,
  columns = ['*'] as C,
  enabled = true,
  idColumn,
  ...rest
}: UseItemQueryPropsT<T, C>): ReturnType<
  typeof useQuery<UseItemQueryResponseT<T, C>, Error, UseItemQueryResponseT<T, C>, QueryKey>
> => {
  const queryFn: QueryFunction<UseItemQueryResponseT<T, C>, QueryKey> = async () => {
    return fetchItem<T, C>(table, id, columns, idColumn);
  };

  // Options for the query
  const queryOptions: UseQueryOptions<UseItemQueryResponseT<T, C>, Error, UseItemQueryResponseT<T, C>, QueryKey> = {
    enabled,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retryOnMount: false,
    ...rest,
    queryKey: getQueryKey.detail(table, id, columns, enabled),
    queryFn,
  };

  return useQuery(queryOptions);
};
